"use client";
import { useEffect, useState, useCallback, useMemo } from "react";
import Link from "next/link";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import GridViewOutlined from "@mui/icons-material/GridViewOutlined";
import NotificationsNone from "@mui/icons-material/NotificationsNone";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import { Avatar, Tooltip } from "@mui/material";
import RecentChats from "./RecentChats";
import { ensureUserId } from "@/lib/api";
import SettingsPanel from "./SettingsPanel";
import ExplorePanel from "./ExplorePanel";
import NotificationsPanel from "./NotificationsPanel";
import ProfilePanel from "./ProfilePanel";

export default function Sidebar() {
  const [chatOpen, setChatOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [wasAnyOpen, setWasAnyOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [hasAlert, setHasAlert] = useState<boolean>(false);

  // Memoize any drawer open state
  const isAnyDrawerOpen = useMemo(() => 
    chatOpen || settingsOpen || exploreOpen || profileOpen || notificationsOpen,
    [chatOpen, settingsOpen, exploreOpen, profileOpen, notificationsOpen]
  );

  useEffect(() => {
    const body = document.body;
    const width = isAnyDrawerOpen ? "min(360px, 92vw)" : "0px";
    body.style.setProperty("--chat-drawer-offset", width);
    return () => body.style.setProperty("--chat-drawer-offset", "0px");
  }, [isAnyDrawerOpen]);

  // Track if any drawer was already open on the previous frame.
  useEffect(() => {
    setWasAnyOpen((prev) => isAnyDrawerOpen || prev);
    if (!isAnyDrawerOpen) {
      // Reset tracker when both are closed so next open animates once
      setWasAnyOpen(false);
    }
  }, [isAnyDrawerOpen]);

  // Ensure we have a user id cached after login for downstream API calls
  useEffect(() => {
    ensureUserId().catch(() => {});
  }, []);

  // Helper: only one drawer open at a time
  const openPanel = useCallback((panel: 'chat' | 'explore' | 'settings' | 'notifications' | 'profile' | null) => {
    setChatOpen(panel === 'chat');
    setExploreOpen(panel === 'explore');
    setSettingsOpen(panel === 'settings');
    setNotificationsOpen(panel === 'notifications');
    setProfileOpen(panel === 'profile');
  }, []);

  // Load avatar/name and keep in sync
  useEffect(() => {
    const load = () => {
      try {
        setAvatarUrl(localStorage.getItem("user_avatar_url"));
        setDisplayName(localStorage.getItem("user_name"));
      } catch {}
    };
    load();
    const onStorage = () => load();
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Alert dot: show only when credit <= threshold
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod = await import("@/lib/credits");
        const [b, l, s] = await Promise.all([mod.getBalance(), mod.getCreditLimit(), mod.getSettings()]);
        const available = b + Math.max(0, l);
        const threshold = Math.max(1, Number((s as any)?.refillBelow ?? 100));
        if (!cancelled) setHasAlert(available <= threshold);
      } catch { if (!cancelled) setHasAlert(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <aside className="border-solid border-r-1 border-gray-400 fixed left-0 top-0 h-screen w-16 bg-gray-900 text-gray-300 flex flex-col items-center justify-between py-4 z-30 shadow-inner">
      {/* Top logo */}
      <div className="mt-1">
        <Link href="/">
          <img
            src="/img/ChatGPT%20Image%2010%20%E0%B8%95.%E0%B8%84.%202568%2015_51_41.png"
            alt="logo"
            className="w-10 h-10 object-contain"
          />
        </Link>
      </div>

      {/* Icon stack */}
      <nav className="flex flex-col items-center gap-5">
        <button
          title="Chat"
          aria-pressed={chatOpen}
          onClick={() => openPanel(chatOpen ? null : 'chat')}
          className={`relative flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
            chatOpen
              ? "bg-gray-800 text-white"
              : "hover:bg-gray-800/70 hover:text-white"
          }`}
        >
          <ChatBubbleOutline />
        </button>

        <button
          title="Explore"
          aria-pressed={exploreOpen}
          onClick={() => openPanel(exploreOpen ? null : 'explore')}
          className={`relative flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
            exploreOpen ? "bg-gray-800 text-white" : "hover:bg-gray-800/70 hover:text-white"
          }`}
        >
          <GridViewOutlined />
        </button>

        <button
          title="Notifications"
          aria-pressed={notificationsOpen}
          onClick={() => openPanel(notificationsOpen ? null : 'notifications')}
          className={`relative flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
            notificationsOpen ? "bg-gray-800 text-white" : "hover:bg-gray-800/70 hover:text-white"
          }`}
        >
          <NotificationsNone />
          {hasAlert ? <span className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-orange-400"></span> : null}
        </button>

        <button
          title="Settings"
          aria-pressed={settingsOpen}
          onClick={() => openPanel(settingsOpen ? null : 'settings')}
          className={`relative flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
            settingsOpen
              ? "bg-gray-800 text-white"
              : "hover:bg-gray-800/70 hover:text-white"
          }`}
        >
          <SettingsOutlined />
        </button>

        <Link
          href="/about"
          title="Info"
          className="relative flex items-center justify-center w-10 h-10 rounded-md hover:bg-gray-800/70 hover:text-white transition-colors"
        >
          <InfoOutlined />
        </Link>
      </nav>

      {/* Bottom profile */}
      <button
        title="Profile"
        onClick={() => openPanel(profileOpen ? null : 'profile')}
        className="mb-1 flex items-center justify-center w-10 h-10 rounded-full bg-gray-800 text-gray-200 hover:ring-2 hover:ring-indigo-400 overflow-hidden"
      >
        {avatarUrl ? (
          <Avatar src={avatarUrl || undefined} sx={{ width: 40, height: 40 }} />
        ) : (
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#ffffff", color: "#111827", fontSize: 16, border: '1px solid #e5e7eb' }}>
            {(displayName?.[0] || 'U').toUpperCase()}
          </Avatar>
        )}
      </button>

      {/* Recent Chats Drawer overlay */}
      {chatOpen && (
        <>
          <div
            className="fixed inset-0 left-16 z-40 animate-overlay-in bg-black/20 cursor-pointer"
            onClick={() => openPanel(null)}
          />
          <div className={`fixed left-16 top-0 h-screen w-[360px] max-w-[92vw] bg-gray-900 z-50 border-r border-gray-700 shadow-2xl ${!wasAnyOpen ? 'animate-drawer-in' : ''}`}>
            <div className="absolute right-2 top-2 z-50">
              <button
                onClick={() => openPanel(null)}
                className="h-8 w-8 rounded bg-gray-800 text-gray-200 hover:bg-gray-700"
                aria-label="Close"
              >
                x
              </button>
            </div>
            <RecentChats />
          </div>
        </>
      )}

      {/* Settings Drawer overlay */}
      {settingsOpen && (
        <>
          <div
            className="fixed inset-0 left-16 z-40 animate-overlay-in bg-black/20 cursor-pointer"
            onClick={() => openPanel(null)}
          />
          <div className={`fixed left-16 top-0 h-screen w-[360px] max-w-[92vw] bg-gray-900 z-50 border-r border-gray-700 shadow-2xl ${!wasAnyOpen ? 'animate-drawer-in' : ''}`}>
            <div className="absolute right-2 top-2 z-50">
              <button
                onClick={() => openPanel(null)}
                className="h-8 w-8 rounded bg-gray-800 text-gray-200 hover:bg-gray-700"
                aria-label="Close"
              >
                x
              </button>
            </div>
            <SettingsPanel />
          </div>
        </>
      )}

      {/* Explore Drawer overlay */}
      {exploreOpen && (
        <>
          <div
            className="fixed inset-0 left-16 z-40 animate-overlay-in bg-black/20 cursor-pointer"
            onClick={() => openPanel(null)}
          />
          <div className={`fixed left-16 top-0 h-screen w-[360px] max-w-[92vw] bg-gray-900 z-50 border-r border-gray-700 shadow-2xl ${!wasAnyOpen ? 'animate-drawer-in' : ''}`}>
            <div className="absolute right-2 top-2 z-50">
              <button
                onClick={() => openPanel(null)}
                className="h-8 w-8 rounded bg-gray-800 text-gray-200 hover:bg-gray-700"
                aria-label="Close"
              >
                x
              </button>
            </div>
            <ExplorePanel />
          </div>
        </>
      )}

      {/* Notifications Drawer overlay */}
      {notificationsOpen && (
        <>
          <div
            className="fixed inset-0 left-16 z-40 animate-overlay-in bg-black/20 cursor-pointer"
            onClick={() => openPanel(null)}
          />
          <div className={`fixed left-16 top-0 h-screen w-[360px] max-w-[92vw] bg-gray-900 z-50 border-r border-gray-700 shadow-2xl ${!wasAnyOpen ? 'animate-drawer-in' : ''}`}>
            <div className="absolute right-2 top-2 z-50">
              <button
                onClick={() => openPanel(null)}
                className="h-8 w-8 rounded bg-gray-800 text-gray-200 hover:bg-gray-700"
                aria-label="Close"
              >
                x
              </button>
            </div>
            <NotificationsPanel />
          </div>
        </>
      )}

      {/* Profile Drawer overlay */}
      {profileOpen && (
        <>
          <div
            className="fixed inset-0 left-16 z-40 animate-overlay-in bg-black/20 cursor-pointer"
            onClick={() => openPanel(null)}
          />
          <div className={`fixed left-16 top-0 h-screen w-[360px] max-w-[92vw] bg-gray-900 z-50 border-r border-gray-700 shadow-2xl ${!wasAnyOpen ? 'animate-drawer-in' : ''}`}>
            <div className="absolute right-2 top-2 z-50">
              <button
                onClick={() => openPanel(null)}
                className="h-8 w-8 rounded bg-gray-800 text-gray-200 hover:bg-gray-700"
                aria-label="Close"
              >
                x
              </button>
            </div>
            <ProfilePanel />
          </div>
        </>
      )}
    </aside>
  );
}





